import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { useSeenMarker } from '@/hooks/useSeenMarker';
import { RELEASES } from '@/lib/releases';

const SEEN_KEY = 'whats-new:seen';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * «Что нового» — список изменений админки (см. RELEASES). Красная точка горит,
 * пока пользователь не открыл список с последней записью, и загорается снова,
 * когда появляется новая.
 */
export function WhatsNewMenu() {
  const [open, setOpen] = useState(false);
  const { seen, markSeen } = useSeenMarker(SEEN_KEY);
  const wrapRef = useRef<HTMLDivElement>(null);

  const latest = RELEASES[0];
  const hasUnread = latest !== undefined && seen !== latest.id;

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const toggle = () => {
    if (!open && latest) markSeen(latest.id);
    setOpen((v) => !v);
  };

  return (
    <div className="topbar__menu-wrap" ref={wrapRef}>
      <button className="topbar__icon-btn" type="button" aria-label="Что нового" onClick={toggle}>
        <Icon name="gift" size={17} />
        {hasUnread && <span className="topbar__dot" />}
      </button>

      {open && (
        <div className="topbar__dropdown topbar__dropdown--wide">
          <div className="topbar__dropdown-head">Что нового</div>

          <div className="whats-new">
            {RELEASES.map((release, index) => (
              <article className="whats-new__item" key={release.id}>
                <header className="whats-new__head">
                  <span className="whats-new__date">{formatDate(release.date)}</span>
                  {index === 0 && <span className="badge badge--info">Новое</span>}
                </header>
                <h3 className="whats-new__title">{release.title}</h3>
                <p className="whats-new__desc">{release.description}</p>
                <ul className="whats-new__details">
                  {release.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
